import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBox, faHandshake } from '@fortawesome/free-solid-svg-icons';
import StockItemForm from '../StockItemForm';
import StockItemVariantForm from '../StockItemVariantForm';
import ServiceQuickForm from './ServiceQuickForm';
import axiosInstance from '../../utils/axiosConfig';

/**
 * Inline Product Creator (Sprint D — Invoice Capture Redesign)
 *
 * Tiny orchestrator that runs the canonical 2-step product creation flow
 * (StockItemForm → StockItemVariantForm) inside a calling surface (e.g. an
 * invoice line item, a delivery form, a recap product picker) so the user
 * never has to leave the form they were filling in just to create a missing
 * product.
 *
 * This component is NOT a third modal. It renders the same two modals
 * StockModule.js renders, in the same sequence StockModule.js renders them.
 * It just lifts the orchestration state into one shared place so the 8
 * consumer surfaces don't each re-implement the same state machine.
 *
 * Wiring contract:
 *   - onProductCreated(variant) fires when StockItemVariantForm POST succeeds.
 *     The caller is responsible for attaching variant.id to whatever line
 *     item / field triggered the flow.
 *   - onClose fires when the user cancels at any step. The caller should
 *     leave their form state untouched on cancel.
 *
 * Pre-fill props ride from the calling line item into the two modals:
 *   - initialSearchTerm seeds StockItemForm.subtype_label and
 *     StockItemVariantForm.shortname.
 *   - initialPriceField selects which price field
 *     (purchase_price for AP surfaces, sale_price for AR surfaces) gets
 *     initialUnitPrice — important because /api/stock/variants/search/
 *     filters by the matching price > 0 and the new variant must remain
 *     findable on the surface that created it.
 */
const InlineProductCreator = ({
    isOpen,
    onClose,
    onProductCreated,
    // HMS request — when allowService is set, the flow opens with a small
    // "Product or Service?" chooser. Picking Service runs ServiceQuickForm
    // instead of the 2-step product flow and fires onServiceCreated(service)
    // on success. Default off so product-only surfaces (delivery, recaps,
    // offer, fuel enquiry) keep their existing single-purpose behaviour.
    allowService = false,
    onServiceCreated,
    initialServiceDescription = '',
    initialSearchTerm = '',
    initialCategory = 'coal',
    // Sprint 7A: when set, pre-fills + locks the StockItemForm's
    // product_type dropdown (e.g. 'mixed_fuels' for fuels surfaces).
    // Pass-through to StockItemForm; that form already accepts the
    // prop and treats it as the initial / locked subtype.
    initialProductType = '',
    initialSupplierName = '',
    initialPriceField = 'purchase_price',
    initialUnitPrice = '',
    initialCurrency = 'USD',
    initialUnitCode = null,
    initialQuantity = '',
}) => {
    const [step, setStep] = useState('item');
    // The product typed at step 1, held in memory (never written) until the
    // item step submits both halves in one atomic POST.
    const [pendingStockItem, setPendingStockItem] = useState(null);
    const [parentSubtype, setParentSubtype] = useState(null);
    // "Use this one instead": the product the duplicate warning offered, and
    // its existing items. The caller's onProductCreated contract is a VARIANT,
    // so an existing product cannot simply be handed back -- the user has to
    // land on one of its items, or add a new item to it.
    const [existingProduct, setExistingProduct] = useState(null);
    const [existingVariants, setExistingVariants] = useState({ status: 'idle', items: [] });

    useEffect(() => {
        if (isOpen) {
            setStep(allowService ? 'choose' : 'item');
            setPendingStockItem(null);
            setParentSubtype(null);
            setExistingProduct(null);
            setExistingVariants({ status: 'idle', items: [] });
        }
    }, [isOpen, allowService]);

    // 2026-05-23 fix: memoise the pre-fill objects so their reference is
    // stable across parent re-renders. Without useMemo, every render of the
    // calling surface (e.g. ProformaInvoiceScreen2, which re-renders on
    // focus / autosave / sibling state churn) rebuilt these literals,
    // which then re-triggered StockItemVariantForm's hydration useEffect
    // (deps include ``preFilledData``) and wiped the user's typed values
    // back to the pre-fill defaults the moment focus left an input.
    //
    // These hooks MUST be declared before the ``if (!isOpen) return null``
    // early return below, otherwise React's rules-of-hooks fires (hooks
    // called conditionally across renders).
    const itemPreFill = useMemo(
        () => ({ subtype_label: initialSearchTerm || '' }),
        [initialSearchTerm],
    );

    // Sprint D follow-up — the stock backend defines quantity / purchase_price /
    // sale_price as DecimalField(decimal_places=2). When the calling surface
    // pre-fills these from a captured invoice line, the LLM may emit values
    // with 3+ decimals (e.g. quantity "800.000"), which causes DRF to reject
    // the variant POST with "Ensure that there are no more than 2 decimal
    // places.". Clamp every numeric pre-fill to at most 2dp on the way in so
    // the save path is always valid; the user can still edit afterwards.
    //
    // Build variant pre-fill conditionally — the StockItemVariantForm
    // hydration effect treats an absent preFilledData differently from one
    // with empty fields, so only set keys we have actual values for.
    const variantPreFill = useMemo(() => {
        const clamp2dp = (v) => {
            if (v === '' || v === null || v === undefined) return '';
            const num = parseFloat(String(v).replace(/,/g, ''));
            if (Number.isNaN(num)) return '';
            return num.toFixed(2);
        };
        const pf = {
            shortname: initialSearchTerm || '',
            unit: initialUnitCode || null,
            price_currency: initialCurrency || 'USD',
            quantity: clamp2dp(initialQuantity),
        };
        if (initialPriceField === 'sale_price') {
            pf.sale_price = clamp2dp(initialUnitPrice);
        } else {
            pf.purchase_price = clamp2dp(initialUnitPrice);
        }
        return pf;
    }, [
        initialSearchTerm,
        initialUnitCode,
        initialCurrency,
        initialQuantity,
        initialPriceField,
        initialUnitPrice,
    ]);

    if (!isOpen) return null;

    const handleItemCreated = (payload) => {
        // Deferred mode: nothing has been written yet. We hold the product and
        // the item step POSTs both together, so cancelling at step 2 can't
        // leave a product with no items — invisible to every picker
        // (invoice-product-search-silent-drops, 2026-08-19).
        if (!payload || !payload.draft) {
            console.warn('[InlineProductCreator] StockItemForm onSuccess fired without a draft; aborting.');
            onClose?.();
            return;
        }
        setPendingStockItem(payload.draft);
        setParentSubtype(payload.product_type || null);
        setStep('variant');
    };

    /**
     * "Use this one instead" on the duplicate warning.
     *
     * The caller was promised a variant, and a product on its own is something
     * it cannot attach to a line item. So this opens the existing product's
     * items: one item is picked straight away, several are offered as a choice,
     * and none sends the user to the item step for that product.
     */
    const handleUseExisting = async (match) => {
        if (!match || !match.id) return;
        setExistingProduct(match);
        setParentSubtype(match.product_type || null);
        setPendingStockItem(null);
        setStep('existing');
        await loadExistingVariants(match);
    };

    const loadExistingVariants = async (match) => {
        setExistingVariants({ status: 'loading', items: [] });
        try {
            const response = await axiosInstance.get(
                `/api/stock/stock-variants/?stock_item_id=${match.id}`,
            );
            const data = response.data;
            const items = Array.isArray(data) ? data : (data?.results || []);
            if (items.length === 0) {
                // Nothing to choose between -- go straight to adding the first
                // item to this existing product.
                setExistingVariants({ status: 'loaded', items: [] });
                setStep('variant');
                return;
            }
            setExistingVariants({ status: 'loaded', items });
        } catch (err) {
            console.error('[InlineProductCreator] Failed to load the existing product\'s items.', err);
            // Say so. Falling through to "this product has no items" would send
            // the user off to create a second copy of an item that exists.
            setExistingVariants({ status: 'failed', items: [] });
        }
    };

    const handleVariantCreated = async (variant) => {
        if (!variant || !variant.id) {
            console.warn('[InlineProductCreator] StockItemVariantForm onSuccess fired without a variant payload.');
            onClose?.();
            return;
        }

        // 2026-05-23 fix: POST /api/stock/stock-variants/ returns the base
        // StockItemVariantSerializer payload which does NOT include
        // display_name / full_description / unit_id. Calling surfaces
        // (e.g. ProformaInvoiceScreen2.handleSelectVariant) read exactly
        // those fields to render the line item's product description, so
        // selecting a freshly created variant left the description column
        // blank. The retrieve action on the same viewset *does* return
        // StockVariantForInvoiceSerializer (display_name + full_description
        // + unit_id present), so a quick GET enriches the payload to match
        // what the search dropdown would have handed back.
        try {
            const enriched = await axiosInstance.get(
                `/api/stock/stock-variants/${variant.id}/`,
            );
            onProductCreated?.(enriched.data);
        } catch (err) {
            console.error('[InlineProductCreator] Failed to re-fetch newly created variant; falling back to raw POST payload.', err);
            onProductCreated?.(variant);
        }
    };

    const handleServiceCreated = (service) => {
        if (!service || !service.id) {
            console.warn('[InlineProductCreator] ServiceQuickForm onSuccess fired without a service payload.');
            onClose?.();
            return;
        }
        onServiceCreated?.(service);
    };

    const chooserButtonStyle = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '22px 14px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        background: '#fff',
        cursor: 'pointer',
    };

    return (
        <>
            {step === 'choose' && (
                <div className="modal-overlay">
                    <div className="modal-content stock-form-modal" style={{ maxWidth: '480px' }}>
                        <div className="modal-header">
                            <h2>
                                <span className="modal-icon-badge">
                                    <FontAwesomeIcon icon={faBox} />
                                </span>
                                What would you like to add?
                            </h2>
                            <button type="button" className="close-button" onClick={onClose}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: '16px', padding: '24px' }}>
                            <button
                                type="button"
                                onClick={() => setStep('item')}
                                style={chooserButtonStyle}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#eff6ff'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fff'; }}
                            >
                                <FontAwesomeIcon icon={faBox} style={{ fontSize: '28px', marginBottom: '10px', color: '#3b82f6' }} />
                                <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>Product</span>
                                <span style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', textAlign: 'center' }}>Physical goods — coal, fuels, metals…</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setStep('service')}
                                style={chooserButtonStyle}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#eff6ff'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fff'; }}
                            >
                                <FontAwesomeIcon icon={faHandshake} style={{ fontSize: '28px', marginBottom: '10px', color: '#3b82f6' }} />
                                <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>Service</span>
                                <span style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', textAlign: 'center' }}>Freight, inspection, insurance…</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {step === 'service' && (
                <ServiceQuickForm
                    isOpen={true}
                    onClose={onClose}
                    onSuccess={handleServiceCreated}
                    initialDescription={initialServiceDescription || initialSearchTerm}
                />
            )}

            {step === 'item' && (
                <StockItemForm
                    isOpen={true}
                    onClose={onClose}
                    onSuccess={handleItemCreated}
                    deferSubmit
                    initialCategory={initialCategory}
                    initialProductType={initialProductType}
                    preFilledData={itemPreFill}
                    onUseExisting={handleUseExisting}
                />
            )}

            {/* The existing product's items. The calling surface attaches an
              * ITEM to its line, never a product, so this is where "use the one
              * we already have" actually completes. */}
            {step === 'existing' && existingProduct && (
                <div className="modal-overlay">
                    <div className="modal-content stock-form-modal" style={{ maxWidth: '560px' }}>
                        <div className="modal-header">
                            <h2>
                                <span className="modal-icon-badge">
                                    <FontAwesomeIcon icon={faBox} />
                                </span>
                                {existingProduct.display_name}
                            </h2>
                            <button type="button" className="close-button" onClick={onClose}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div style={{ padding: '20px 24px' }}>
                            {existingVariants.status === 'loading' && (
                                <p style={{ color: '#6b7280' }}>Loading this product's items…</p>
                            )}

                            {existingVariants.status === 'failed' && (
                                <div role="alert" style={{ color: '#b91c1c', marginBottom: '16px' }}>
                                    We could not load this product's items. Try again, or add a
                                    new item to it.
                                </div>
                            )}

                            {existingVariants.status === 'loaded' && existingVariants.items.length > 0 && (
                                <>
                                    <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: 0 }}>
                                        Pick the item you need.
                                    </p>
                                    {existingVariants.items.map((variant) => (
                                        <div
                                            key={variant.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '12px',
                                                padding: '10px 0',
                                                borderTop: '1px solid #e5e7eb',
                                            }}
                                        >
                                            <span style={{ fontWeight: 600, color: '#111827' }}>
                                                {variant.shortname || 'Item'}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => handleVariantCreated(variant)}
                                            >
                                                Use this item
                                            </button>
                                        </div>
                                    ))}
                                </>
                            )}

                            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                                {existingVariants.status === 'failed' && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => loadExistingVariants(existingProduct)}
                                    >
                                        Try again
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setStep('variant')}
                                >
                                    Add a new item to this product
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {step === 'variant' && (pendingStockItem || existingProduct) && (
                <StockItemVariantForm
                    isOpen={true}
                    onClose={onClose}
                    onSuccess={handleVariantCreated}
                    pendingStockItem={pendingStockItem}
                    stockItemId={existingProduct ? existingProduct.id : undefined}
                    parentSubtype={parentSubtype}
                    preFilledData={variantPreFill}
                />
            )}
        </>
    );
};

export default InlineProductCreator;
